import {
	AccountLinkDTO,
	GoogleOAuthAccountLinkDTO,
	MCCAccountLinkDTO
} from 'api-models'
import { ApiClient } from '../api-client'

async function find(productAccountId: string): Promise<Array<AccountLinkDTO>> {
	const { data } = await ApiClient.client.get(
		`/accounts/${productAccountId}/links`
	)
	return data.data
}

async function getById(
	productAccountId: string,
	id: string
): Promise<AccountLinkDTO> {
	const { data } = await ApiClient.client.get(
		`/accounts/${productAccountId}/links/${id}`
	)
	return data.data
}

async function deleteLink(
	productAccountId: string,
	linkId: string,
	deactivatePPC: boolean
): Promise<AccountLinkDTO> {
	const { data } = await ApiClient.client.delete(
		`/accounts/${productAccountId}/links/${linkId}?deactivate=${deactivatePPC}`
	)
	return data.data
}

async function syncLink(
	productAccountId: string,
	linkId: string
): Promise<AccountLinkDTO> {
	const { data } = await ApiClient.client.post(
		`/accounts/${productAccountId}/links/${linkId}/sync`
	)
	return data.data
}

async function createOAuthLink(
	provider: 'google' | 'facebook' | 'bing',
	payload: { auth_code: string },
	productAccountId?: string | null
): Promise<GoogleOAuthAccountLinkDTO> {
	const { data } = await ApiClient.client.post(
		`/accounts/${productAccountId}/links/oauth/${provider}`,
		payload
	)
	return data.data
}

async function createMccLink(
	productAccountId: string,
	payload: { account_number: string; parent_id?: string | null }
): Promise<MCCAccountLinkDTO> {
	const { data } = await ApiClient.client.post(
		`/accounts/${productAccountId}/links/mcc`,
		payload
	)
	return data.data
}

// Shopify conversion-tracking app (DEV-2365). Starts OAuth: the backend returns a Shopify
// authorize URL (with a server-generated state nonce) for the browser to redirect to.
async function shopifyAuthorize(
	productAccountId: string,
	payload: { shop: string; asset_id: string }
): Promise<{ authorize_url: string }> {
	const { data } = await ApiClient.client.post(
		`/accounts/${productAccountId}/links/oauth/shopify/authorize`,
		payload
	)
	return data.data
}

export interface ShopifyLinkStatusDTO {
	id: string
	shop_domain: string
	status: 'ACTIVE' | 'NEEDS_REAUTH' | 'DISCONNECTED'
	// Whether the storefront app-embed is enabled (null if never checked). Deep link to enable it.
	embed_active: boolean | null
	embed_activate_url: string
}

// Current Shopify connection for an asset (or null). Drives the connected state + connect-flow polling.
async function getShopifyLink(
	productAccountId: string,
	assetId: string
): Promise<ShopifyLinkStatusDTO | null> {
	const { data } = await ApiClient.client.get(
		`/accounts/${productAccountId}/links/shopify`,
		{ params: { asset_id: assetId } }
	)
	return data.data
}

// Live re-check of a link (status + embed). Polled after the merchant enables + saves the embed.
async function recheckShopify(
	productAccountId: string,
	linkId: string
): Promise<ShopifyLinkStatusDTO> {
	const { data } = await ApiClient.client.post(
		`/accounts/${productAccountId}/links/shopify/${linkId}/recheck`
	)
	return data.data
}

// In-platform disconnect: removes the Web Pixel + metafields and marks the link DISCONNECTED.
async function disconnectShopify(
	productAccountId: string,
	linkId: string
): Promise<null> {
	const { data } = await ApiClient.client.delete(
		`/accounts/${productAccountId}/links/shopify/${linkId}`
	)
	return data.data
}

export interface ShopifyShopStatusDTO {
	shop_domain: string
	// A Shopify-initiated install (from the Shopify admin/App Store) is held, waiting to be bound.
	pending: boolean
	// The shop is already bound to a (non-disconnected) ClickGUARD link.
	bound: boolean
	// The account/asset the shop is bound to (null when not bound). The claim page checks the account
	// against the current user's workspaces to tell owner (→ settings) from stranger (→ blocked).
	boundAccountId: string | null
	boundAssetId: string | null
}

// The Shopify-side install link for the "Install app" button (DEV-2404). Served from the backend
// shopify config so all Shopify configuration lives in one place + stays environment-correct.
async function getShopifyInstallUrl(productAccountId: string): Promise<{ install_url: string }> {
	const { data } = await ApiClient.client.get(
		`/accounts/${productAccountId}/links/shopify/install-url`
	)
	return data.data
}

// Resolve a shop's connection status for the claim page (DEV-2365 §2.3).
async function getShopifyStatus(
	productAccountId: string,
	shop: string
): Promise<ShopifyShopStatusDTO> {
	const { data } = await ApiClient.client.get(
		`/accounts/${productAccountId}/links/shopify/status`,
		{ params: { shop } }
	)
	return data.data
}

// Bind a pending Shopify-initiated install to the chosen asset — the backend materializes the link
// from the held token and wires the Web Pixel + metafields.
async function claimShopify(
	productAccountId: string,
	payload: { shop: string; asset_id: string }
): Promise<{ id: string; shop_domain: string; status: string; asset_id: string }> {
	const { data } = await ApiClient.client.post(
		`/accounts/${productAccountId}/links/shopify/claim`,
		payload
	)
	return data.data
}

export const AdAccountLinks = {
	find,
	getById,
	createOAuthLink,
	createMccLink,
	deleteLink,
	syncLink,
	shopifyAuthorize,
	getShopifyLink,
	recheckShopify,
	disconnectShopify,
	getShopifyInstallUrl,
	getShopifyStatus,
	claimShopify
}
